import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { useMemo, useState } from 'react'
import { v1 as uuidv1 } from 'uuid'

import {
  ArtistMembershipFavoriteMembersIdFragmentDoc,
  ArtistMembershipFavoriteMembersSettingsFragmentDoc,
  ArtistMembershipForGuestQuery,
  ArtistMembershipForGuestQueryVariables,
  ArtistMembershipQuery,
  ArtistMembershipQueryVariables,
  ArtistMembershipSubscriptionDetailFragmentDoc,
  ArtistWithSubscriptionFragmentDoc,
  CancelDowngradeSubscriptionPlanProcessMutation,
  CancelDowngradeSubscriptionPlanProcessMutationVariables,
  CreateIapSubscriptionMutation,
  CreateIapSubscriptionMutationVariables,
  DowngradeSubscriptionPlanProcessMutation,
  DowngradeSubscriptionPlanProcessMutationVariables,
  Maybe,
  MembershipSubscription,
  PatronDetailFragmentDoc,
  PaymentDetailFragmentDoc,
  PreviewSubscriptionUpdateQuery,
  PreviewSubscriptionUpdateQueryVariables,
  StopSubscriptionProcess,
  StopSubscriptionProcessMutation,
  StopSubscriptionProcessMutationVariables,
  TryPaymentLockMutation,
  TryPaymentLockMutationVariables,
  UpgradeSubscriptionPlanProcessMutation,
  UpgradeSubscriptionPlanProcessMutationVariables,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { getActiveMembershipSubscriptionProductPlan } from '@/core/membership'
import { useSettingUserInfoModal } from '@/core/membership/useSettingUserInfoModal'
import { App } from '@/core/native'
import { getPlatform } from '@/core/native/app'
import { setRegisterNewMembership } from '@/core/native/storage'
import { getSubscriptionById, purchase } from '@/core/payment'
import {
  isCarrierPaymentMethod,
  submitEscottForm,
  useBFCacheRestore,
} from '@/lib/escott'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { router } = useRouter<{ artistId: string }>()
  const { t } = useTranslation('artistMembership')
  const { showModal } = useSettingUserInfoModal()
  const artistId = router.query.artistId ?? config.artistId

  const isWeb = App.getPlatform() === 'web'
  const isIOs = App.getPlatform() === 'ios'

  const [nativeSubscribing, setNativeSubscribing] = useState(false)

  const { data, loading } = useQuery<
    ArtistMembershipQuery,
    ArtistMembershipQueryVariables
  >(QUERY, {
    variables: { id: artistId },
    skip: !user,
  })
  const { data: dataGuest, loading: loadingGuest } = useQuery<
    ArtistMembershipForGuestQuery,
    ArtistMembershipForGuestQueryVariables
  >(QUERY_FOR_GUEST, {
    variables: { id: artistId },
    skip: !!user,
  })
  const [
    previewSubscriptionUpdate,
    {
      data: previewSubscriptionUpdateData,
      loading: fetchingDiffAmountSubscription,
    },
  ] = useLazyQuery<
    PreviewSubscriptionUpdateQuery,
    PreviewSubscriptionUpdateQueryVariables
  >(PREVIEW_SUBSCRIPTION_UPDATE)

  // 外部キャリア決済画面（escott）へのリダイレクト中かどうかを管理するフラグ
  // mutation の完了から escott フォーム送信までに
  // submit ボタンが活性化してしまうのを防ぐために用いている
  const [isRedirectingToEscott, setIsRedirectingToEscott] = useState(false)

  const [
    upgradeSubscriptionPlanProcess,
    { loading: upgradingSubscriptionPlan },
  ] = useMutation<
    UpgradeSubscriptionPlanProcessMutation,
    UpgradeSubscriptionPlanProcessMutationVariables
  >(UPGRADE_SUBSCRIPTION_PLAN, {
    refetchQueries: () => [{ query: QUERY, variables: { id: artistId } }],
    awaitRefetchQueries: true,
    onCompleted({ upgradeSubscriptionPlanProcess }) {
      switch (upgradeSubscriptionPlanProcess.__typename) {
        // 追加手続きが不要なケース
        // ステータスの更新された Patron が返される
        case 'Patron':
          break
        // 外部キャリア決済画面にて追加手続きが必要なケース
        // キャリア決済を想定している
        case 'EscottMasterResult':
          // 外部のキャリア決済画面へフォーム送信によってリダイレクト
          // 後続処理が外部画面とバックエンドで実行された後に
          // ステータスに応じて escottInput で指定した画面に復帰する
          submitEscottForm(
            upgradeSubscriptionPlanProcess.nextActionUrl,
            upgradeSubscriptionPlanProcess.parameter
          )
          break
        default:
          break
      }
    },
  })

  const [
    downgradeSubscriptionPlanProcess,
    { loading: downgradingSubscriptionPlan },
  ] = useMutation<
    DowngradeSubscriptionPlanProcessMutation,
    DowngradeSubscriptionPlanProcessMutationVariables
  >(DOWNGRADE_SUBSCRIPTION_PLAN_PROCESS, {
    refetchQueries: () => [{ query: QUERY, variables: { id: artistId } }],
    awaitRefetchQueries: true,
    onCompleted({ downgradeSubscriptionPlanProcess }) {
      switch (downgradeSubscriptionPlanProcess.__typename) {
        // 追加手続きが不要なケース
        // ステータスの更新された Patron が返される
        case 'Patron':
          break
        // 外部キャリア決済画面にて追加手続きが必要なケース
        // キャリア決済を想定している
        case 'EscottMasterResult':
          // 外部のキャリア決済画面へフォーム送信によってリダイレクト
          // 後続処理が外部画面とバックエンドで実行された後に
          // ステータスに応じて escottInput で指定した画面に復帰する
          submitEscottForm(
            downgradeSubscriptionPlanProcess.nextActionUrl,
            downgradeSubscriptionPlanProcess.parameter
          )
          break
        default:
          break
      }
    },
  })

  /**
   * プラン変更のキャンセル
   * アップグレードのキャンセルもダウングレードのキャンセルも
   * API の実装としては同一の処理であるため
   * cancelDowngradeSubscriptionPlanProcess を参照している
   * ref. https://utoniq.slack.com/archives/C089QSPGB27/p1747637183291179?thread_ts=1747635734.465719&cid=C089QSPGB27
   */
  const [
    cancelChangeSubscriptionPlanProcess,
    { loading: cancelingChangeSubscriptionPlan },
  ] = useMutation<
    CancelDowngradeSubscriptionPlanProcessMutation,
    CancelDowngradeSubscriptionPlanProcessMutationVariables
  >(CANCEL_DOWNGRADE_SUBSCRIPTION_PLAN_PROCESS, {
    refetchQueries: () => [{ query: QUERY, variables: { id: artistId } }],
    awaitRefetchQueries: true,
    onCompleted({ cancelDowngradeSubscriptionPlanProcess }) {
      switch (cancelDowngradeSubscriptionPlanProcess.__typename) {
        // 追加手続きが不要なケース
        // ステータスの更新された Patron が返される
        case 'Patron':
          break
        // 外部キャリア決済画面にて追加手続きが必要なケース
        // キャリア決済を想定している
        case 'EscottMasterResult':
          // 外部のキャリア決済画面へフォーム送信によってリダイレクト
          // 後続処理が外部画面とバックエンドで実行された後に
          // ステータスに応じて escottInput で指定した画面に復帰する
          submitEscottForm(
            cancelDowngradeSubscriptionPlanProcess.nextActionUrl,
            cancelDowngradeSubscriptionPlanProcess.parameter
          )
          break
        default:
          break
      }
    },
  })

  const [stopSubscriptionProcess, { loading: stoppingSubscription }] =
    useMutation<
      StopSubscriptionProcessMutation,
      StopSubscriptionProcessMutationVariables
    >(StopSubscriptionProcess, {
      refetchQueries: () => [{ query: QUERY, variables: { id: artistId } }],
      awaitRefetchQueries: true,
    })

  const [tryPaymentLock] = useMutation<
    TryPaymentLockMutation,
    TryPaymentLockMutationVariables
  >(TRY_PAYMENT_LOCK)
  const [createIapSubscription] = useMutation<
    CreateIapSubscriptionMutation,
    CreateIapSubscriptionMutationVariables
  >(CREATE_IAP_SUBSCRIPTION, {
    async onCompleted({ createIapSubscription }) {
      await setRegisterNewMembership()
      if (createIapSubscription?.isPersonalInformationRequired) {
        showModal({
          showSnackbarWhenClose: true,
          isRegisterNewMembership: true,
        })
      }
    },
  })

  const { artist } = (data ?? dataGuest) || {}

  const appName = useMemo<string | undefined>(
    /**
     * @description
     * Open when function is implemented in the future
     * https://app.clickup.com/t/908127/UT-15031
     */
    //@ts-ignore
    () => getAppNameByPlatformAndSubscription(artist?.subscription),
    [artist?.subscription]
  )

  /**
   * サブスク契約（ネイティブ用）
   */
  const subscribeByNative = async () => {
    setNativeSubscribing(true)

    // revenueCatより該当アーティストのサブスク情報を取得
    const apiKey = App.getPlatformResult({
      ios: config.revenueCatApiKey.ios,
      android: config.revenueCatApiKey.android,
      web: '',
    })
    const productId = await getSubscriptionById(
      artist?.id || '',
      apiKey,
      user?.uid || '',
      isIOs
    )

    // ランダム生成したidでトランザクションのロックをかける
    const uuid = uuidv1()
    const { data: tryLockForNativePurchase } = await tryPaymentLock({
      variables: { uuid, artistId: artist?.id },
    })

    // 二重支払い防止用のロック取得に失敗した場合はエラーを返す
    if (!tryLockForNativePurchase?.tryPaymentLock?.success) {
      throw new Error(t('tryLockForNativePurchaseError'))
    }

    // ネイティブ機能でrevenueCat経由でサブスク契約
    await purchase(
      productId,
      apiKey,
      activeProduct?.plan.amount ?? 0,
      user?.uid,
      true,
      t
    )

    // syncの場合はすでに支払済みのためartistIdを指定しない(指定するとサブスクリプションが存在するためエラーとなる)
    const { data: tryLockForSync } = await tryPaymentLock({
      variables: { uuid },
    })

    // 前段で取得した支払いロックに対し、同じuuidで再度ロックを取得。ロックに失敗した場合はエラーを返す
    if (!tryLockForSync?.tryPaymentLock?.success) {
      throw new Error(t('tryLockForSyncError'))
    }

    // mutationでfirestore側へ決済完了を同期する

    /**
     * @description
     * iOS: liteとcrazy vodka tonicはUtoniqのiOS/revenueCatプロジェクトサブスクリプション登録してあるので、
     * 上記のパターンの時のみappにutoniqを入れる。
     */
    const app =
      isIOs &&
      (config.applicationKey === 'lite' ||
        config.applicationKey === 'crazyvodkatonic')
        ? 'utoniq'
        : config.applicationKey
    return createIapSubscription({
      variables: { productId: productId, uuid, app, artistId },
      refetchQueries: () => [{ query: QUERY, variables: { id: artistId } }],
      awaitRefetchQueries: true,
    })
  }

  const activeProduct = useMemo(() => {
    const selectedPlanId =
      data?.artist?.pendingPurchasePatron?.metadata?.membership?.planId ??
      data?.artist?.patron?.subscription?.planId ??
      (!isWeb
        ? // ネイティブの場合、複数プランに対応していないためplanIdがない。そのためplanｓの先頭のプランを毎回取得する
          // ネイティブが複数プラン対応になった場合、ここも合わせて変更が必要となる。
          data?.artist?.subscription?.products?.[0]?.plans?.[0]?.planId
        : undefined)

    return getActiveMembershipSubscriptionProductPlan(
      data?.artist,
      selectedPlanId
    )
  }, [data?.artist, isWeb])

  const upgradeProduct = useMemo(() => {
    return getActiveMembershipSubscriptionProductPlan(
      data?.artist,
      data?.artist?.patron?.subscription?.upgradePlanId
    )
  }, [data?.artist])

  const downgradeProduct = useMemo(() => {
    return getActiveMembershipSubscriptionProductPlan(
      data?.artist,
      data?.artist?.patron?.subscription?.downgradePlanId
    )
  }, [data?.artist])

  // 外部キャリア決済画面（escott）からブラウザバックした際
  // リダイレクト中かどうかを管理するフラグをリセットする
  // ブラウザバック時に BFCache が効き再読み込みが走らない環境で
  // ローディング状態が解除されないことを防ぐ
  useBFCacheRestore(() => {
    setIsRedirectingToEscott(false)
  })

  return {
    state: {
      isLoggedIn: !!user,
      loading: loading || loadingGuest,
      subscribing:
        upgradingSubscriptionPlan ||
        downgradingSubscriptionPlan ||
        cancelingChangeSubscriptionPlan ||
        stoppingSubscription ||
        nativeSubscribing ||
        isRedirectingToEscott,
      isMember: !!artist?.patron,
      paymentMethod: artist?.patron?.paymentMethod ?? null,
      patronType: artist?.patron?.type ?? null,
      store: artist?.patron?.iapSubscription?.store ?? null,
      /**
       * @description
       * Open when function is implemented in the future
       * https://app.clickup.com/t/908127/UT-15031
       */
      //@ts-ignore
      stopSubscription: artist?.subscription?.status === 'stopped',
      requireRegisteredCredit: isWeb && !data?.paymentSource && !!user,
      artist,
      appName,
      subscription: artist?.patron?.subscription ?? null,
      downgradeAtNextPeriod: !!artist?.patron?.downgradeAtNextPeriod,
      products:
        data?.artist?.subscription?.products ??
        dataGuest?.artist?.subscription?.products ??
        null,
      activeProduct,
      downgradeProduct,
      upgradeProduct,
      isPersonalInformationRequired:
        !!artist?.patron?.isPersonalInformationRequired,
      isMembershipPersonalInformationRequired:
        !!data?.user?.isMembershipPersonalInformationRequired,
      enableMembershipCarrierPayment: !!artist?.subscription?.carrierPayment,
      expiredAtSubscription: artist?.patron?.formattedExpiredAt ?? null,
      pendingPurchasePatron: artist?.pendingPurchasePatron ?? null,
      diffAmountSubscription:
        previewSubscriptionUpdateData?.previewSubscriptionUpdate.diffAmount ??
        0,
      fetchingDiffAmountSubscription,
      selectFavoriteMemberRequired:
        !!data?.artist?.settings?.features?.subscription
          ?.enableFavoriteMembers &&
        (data?.artist?.favoriteMembers?.length ?? 0) > 0,
    },
    actions: {
      // アップデート時に支払う日割差額を取得する
      async onPreviewSubscriptionUpdate(planId) {
        await previewSubscriptionUpdate({
          variables: { artistId, planId },
        })
      },
      // サブスクを開始する
      async onSubscribePlan() {
        if (isWeb) {
          return
        }
        await subscribeByNative().finally(() => setNativeSubscribing(false))
      },
      // 上位プランへのアップグレード
      async onUpgradePlan(planId, paymentMethod, escottInput) {
        // キャリア決済の場合はローディング状態を表す
        // state.subscribing に渡している
        // isRedirectingToEscott を true にする
        // mutation の完了から外部キャリア決済画面に遷移するまでの間に
        // submit ボタンが活性化するのを防ぐために行う
        if (isCarrierPaymentMethod(paymentMethod)) {
          setIsRedirectingToEscott(true)
        }
        await upgradeSubscriptionPlanProcess({
          variables: { artistId, planId, paymentMethod, escottInput },
        })
      },
      // 下位プランへのダウングレード
      async onDowngradePlan(planId, paymentMethod, escottInput) {
        // キャリア決済の場合はローディング状態を表す
        // state.subscribing に渡している
        // isRedirectingToEscott を true にする
        // mutation の完了から外部キャリア決済画面に遷移するまでの間に
        // submit ボタンが活性化するのを防ぐために行う
        if (isCarrierPaymentMethod(paymentMethod)) {
          setIsRedirectingToEscott(true)
        }
        await downgradeSubscriptionPlanProcess({
          variables: { artistId, planId, paymentMethod, escottInput },
        })
      },
      // サブスク自体の停止
      async onStopSubscription() {
        await stopSubscriptionProcess({
          variables: { artistId },
        })
      },
      // プラン変更のキャンセル
      async onCancelChangePlan(paymentMethod, escottInput) {
        // キャリア決済の場合はローディング状態を表す
        // state.subscribing に渡している
        // isRedirectingToEscott を true にする
        // mutation の完了から外部キャリア決済画面に遷移するまでの間に
        // submit ボタンが活性化するのを防ぐために行う
        if (isCarrierPaymentMethod(paymentMethod)) {
          setIsRedirectingToEscott(true)
        }
        await cancelChangeSubscriptionPlanProcess({
          variables: { artistId, paymentMethod, escottInput },
        })
      },
      onClearSelectFavoriteMember() {
        localStorage.removeItem('artistMembershipFavoriteMembersSelect')
      },
    },
  }
})

const QUERY = gql`
  query ArtistMembership($id: ID) {
    artist(id: $id) {
      ...ArtistWithSubscription
      ...ArtistMembershipSubscriptionDetail
      ...ArtistMembershipFavoriteMembersSettings
      ...ArtistMembershipFavoriteMembersId
    }
    user {
      id
      isMembershipPersonalInformationRequired
    }
    paymentSource {
      ...PaymentDetail
    }
  }
  ${ArtistWithSubscriptionFragmentDoc}
  ${ArtistMembershipSubscriptionDetailFragmentDoc}
  ${ArtistMembershipFavoriteMembersSettingsFragmentDoc}
  ${ArtistMembershipFavoriteMembersIdFragmentDoc}
  ${PaymentDetailFragmentDoc}
`

const PREVIEW_SUBSCRIPTION_UPDATE = gql`
  query PreviewSubscriptionUpdate($planId: String!, $artistId: String!) {
    previewSubscriptionUpdate(planId: $planId, artistId: $artistId) {
      planId
      diffAmount
    }
  }
`

const QUERY_FOR_GUEST = gql`
  query ArtistMembershipForGuest($id: ID) {
    artist(id: $id) {
      ...ArtistWithSubscription
      ...ArtistMembershipSubscriptionDetail
    }
  }
  ${ArtistWithSubscriptionFragmentDoc}
  ${ArtistMembershipSubscriptionDetailFragmentDoc}
`

const START_SUBSCRIPTION = gql`
  mutation startSubscription($artistId: ID!, $planId: ID!) {
    startSubscription(artistId: $artistId, planId: $planId) {
      ...PatronDetail
    }
  }
  ${PatronDetailFragmentDoc}
`

const UPGRADE_SUBSCRIPTION_PLAN = gql`
  mutation upgradeSubscriptionPlanProcess(
    $planId: String!
    $artistId: String!
    $paymentMethod: PaymentMethod
    $escottInput: EscottMasterInput
  ) {
    upgradeSubscriptionPlanProcess(
      planId: $planId
      artistId: $artistId
      paymentMethod: $paymentMethod
      escottInput: $escottInput
    ) {
      __typename
      ... on Patron {
        id
        isPersonalInformationRequired
      }
      ... on EscottMasterResult {
        nextActionUrl
        parameter {
          encryptValue
          merchantId
          openID
          operateId
        }
      }
    }
  }
  ${PatronDetailFragmentDoc}
`

const DOWNGRADE_SUBSCRIPTION_PLAN_PROCESS = gql`
  mutation downgradeSubscriptionPlanProcess(
    $planId: String!
    $artistId: String!
    $paymentMethod: PaymentMethod
    $escottInput: EscottMasterInput
  ) {
    downgradeSubscriptionPlanProcess(
      planId: $planId
      artistId: $artistId
      paymentMethod: $paymentMethod
      escottInput: $escottInput
    ) {
      __typename
      ... on Patron {
        id
        isPersonalInformationRequired
      }
      ... on EscottMasterResult {
        nextActionUrl
        parameter {
          encryptValue
          merchantId
          openID
          operateId
        }
      }
    }
  }
  ${PatronDetailFragmentDoc}
`

const CANCEL_DOWNGRADE_SUBSCRIPTION_PLAN_PROCESS = gql`
  mutation cancelDowngradeSubscriptionPlanProcess(
    $artistId: String!
    $paymentMethod: PaymentMethod
    $escottInput: EscottMasterInput
  ) {
    cancelDowngradeSubscriptionPlanProcess(
      artistId: $artistId
      paymentMethod: $paymentMethod
      escottInput: $escottInput
    ) {
      __typename
      ... on Patron {
        id
        isPersonalInformationRequired
      }
      ... on EscottMasterResult {
        nextActionUrl
        parameter {
          merchantId
          operateId
          encryptValue
          openID
        }
      }
    }
  }
  ${PatronDetailFragmentDoc}
`

const TRY_PAYMENT_LOCK = gql`
  mutation tryPaymentLock($uuid: String!, $artistId: String) {
    tryPaymentLock(uuid: $uuid, artistId: $artistId) {
      success
    }
  }
`

const CREATE_IAP_SUBSCRIPTION = gql`
  mutation createIapSubscription(
    $productId: String!
    $uuid: String
    $app: String
    $artistId: String
  ) {
    createIapSubscription(
      productId: $productId
      uuid: $uuid
      app: $app
      artistId: $artistId
    ) {
      ...PatronDetail
    }
  }
  ${PatronDetailFragmentDoc}
`

/**
 * @description return app name by platform and subscription
 * if platform = web return undefined
 * if platform = android and playstore not existed return app name
 * if platform = ios and appstore not existed return app name
 * @param {MembershipSubscription} subscription
 */
export function getAppNameByPlatformAndSubscription(
  subscription?: Maybe<MembershipSubscription>
): string | undefined {
  const platform = getPlatform()
  const url = new URL(config.deepLinkUrl)
  if (platform === 'web') return undefined
  // @note productsは配列だが、ネイティブは現状1プランしか存在できないため、配列の先頭をとる
  if (
    platform === 'android' &&
    !subscription?.products?.[0]?.plans.some(plan => !!plan.playstore)
  ) {
    return url.host
  }
  if (
    platform === 'ios' &&
    !subscription?.products?.[0]?.plans.some(plan => !!plan.appstore)
  ) {
    return url.host
  }
  return undefined
}
